import JSZip from 'jszip';
import html2pdf from 'html2pdf.js';
import { asBlob } from 'html-docx-js-typescript';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/**
 * Utility for bulk downloading multiple documents as a ZIP archive
 */

export interface BulkDownloadOptions {
  documentIds: string[];
  format: 'pdf' | 'word';
  zipFilename: string;
  onProgress?: (current: number, total: number) => void;
  onError?: (documentId: string, error: string) => void;
}

export interface DocumentDownloadResult {
  documentId: string;
  success: boolean;
  blob?: Blob;
  filename?: string;
  title?: string;
  error?: string;
}

/**
 * Strip images and external resources from HTML to prevent loading errors
 */
function stripImagesFromHtml(html: string): string {
  // Remove img tags
  let cleaned = html.replace(/<img[^>]*>/gi, '');
  
  // Remove background-image styles
  cleaned = cleaned.replace(/background-image\s*:\s*url\([^)]*\)\s*;?/gi, '');
  
  // Remove other problematic external resources
  cleaned = cleaned.replace(/<link[^>]*>/gi, '');
  cleaned = cleaned.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
  
  return cleaned;
}

/**
 * Simplify HTML for PDF conversion - remove complex styling that causes rendering issues
 */
function simplifyHtmlForPdf(html: string): string {
  let simplified = html;
  
  // Extract text content more aggressively
  // Remove all <style> blocks
  simplified = simplified.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  
  // Remove all style attributes
  simplified = simplified.replace(/\s+style\s*=\s*["'][^"']*["']/gi, '');
  
  // Remove class and id attributes
  simplified = simplified.replace(/\s+class\s*=\s*["'][^"']*["']/gi, '');
  simplified = simplified.replace(/\s+id\s*=\s*["'][^"']*["']/gi, '');
  
  // Remove divs and spans (keep their content)
  simplified = simplified.replace(/<\/?div[^>]*>/gi, '');
  simplified = simplified.replace(/<\/?span[^>]*>/gi, '');
  
  // Remove empty tags
  simplified = simplified.replace(/<(\w+)[^>]*>\s*<\/\1>/gi, '');
  
  // Normalize whitespace and line breaks
  simplified = simplified.replace(/\s+/g, ' ');
  simplified = simplified.replace(/<br\s*\/?>/gi, '\n');
  
  // Extract body content or use the whole content
  const bodyContent = simplified.match(/<body[^>]*>([\s\S]*)<\/body>/i)?.[1] || simplified;
  
  // Clean the content - convert to plain text structure with basic HTML
  const cleaned = bodyContent
    .trim()
    .replace(/\n\s*\n/g, '\n') // Remove multiple newlines
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .map(line => {
      // Keep headings and paragraphs, wrap plain text
      if (line.match(/^<h[1-6]/i) || line.match(/^<p>/i) || line.match(/^<table/i) || line.match(/^<ul/i) || line.match(/^<ol/i)) {
        return line;
      }
      return `<p>${line}</p>`;
    })
    .join('\n');
  
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'DejaVu Sans', Arial, sans-serif;
      font-size: 11pt;
      line-height: 1.5;
      padding: 15px;
      color: #000;
      background: #fff;
    }
    p { margin: 0.3em 0; }
    h1 { font-size: 16pt; margin: 1em 0 0.5em 0; font-weight: bold; }
    h2 { font-size: 14pt; margin: 0.8em 0 0.4em 0; font-weight: bold; }
    h3 { font-size: 12pt; margin: 0.6em 0 0.3em 0; font-weight: bold; }
    table { border-collapse: collapse; width: 100%; margin: 0.5em 0; }
    td, th { border: 1px solid #333; padding: 4px; text-align: left; font-size: 10pt; }
    ul, ol { margin: 0.3em 0 0.3em 1.5em; }
    li { margin: 0.2em 0; }
  </style>
</head>
<body>
${cleaned}
</body>
</html>`;
}

/**
 * Wrap HTML content with proper UTF-8 encoding for Word conversion
 */
function wrapHtmlForWordConversion(html: string): string {
  // Check if HTML already has proper structure
  if (html.toLowerCase().includes('<!doctype') || html.toLowerCase().includes('<html')) {
    // Already has HTML structure, just ensure UTF-8 meta tag
    if (!html.toLowerCase().includes('charset=utf-8')) {
      html = html.replace(
        /<head>/i,
        '<head><meta charset="UTF-8"><meta http-equiv="Content-Type" content="text/html; charset=UTF-8">'
      );
    }
    return html;
  }
  
  // Wrap content in proper HTML structure with UTF-8 encoding and multilingual font support
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
  <style>
    body { 
      font-family: 'Arial Unicode MS', 'Noto Sans', 'DejaVu Sans', Arial, sans-serif; 
      line-height: 1.6; 
    }
    p { margin: 1em 0; }
  </style>
</head>
<body>
${html}
</body>
</html>`;
}

/**
 * Detect if text is predominantly Right-to-Left (Arabic, Hebrew, etc.)
 */
function detectRTL(text: string): boolean {
  if (!text || text.length === 0) return false;
  
  // Unicode ranges for RTL scripts:
  // Hebrew: \u0590-\u05FF
  // Arabic: \u0600-\u06FF
  // Arabic Supplement: \u0750-\u077F
  // Arabic Extended-A: \u08A0-\u08FF
  const rtlChars = text.match(/[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/g);
  
  // Consider text RTL if more than 30% of characters are RTL
  return rtlChars !== null && rtlChars.length > text.length * 0.3;
}

/**
 * Convert PDF to Word by extracting text and converting to HTML first
 */
async function convertPdfToWord(base64Content: string, title: string): Promise<Blob> {
  try {
    // Decode base64 PDF content
    const pdfData = atob(base64Content);
    const pdfArray = new Uint8Array(pdfData.length);
    for (let i = 0; i < pdfData.length; i++) {
      pdfArray[i] = pdfData.charCodeAt(i);
    }
    
    // Load PDF document
    const loadingTask = pdfjsLib.getDocument({ data: pdfArray });
    const pdf = await loadingTask.promise;
    
    // Extract text from all pages
    const htmlParts: string[] = [];
    
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      
      // Extract text items and maintain some structure
      const textItems = textContent.items as any[];
      
      // Group text items by approximate Y position (lines)
      const lines: { [key: number]: string[] } = {};
      textItems.forEach((item) => {
        if (item.str && item.str.trim()) {
          const y = Math.round(item.transform[5]); // Y position
          if (!lines[y]) lines[y] = [];
          lines[y].push(item.str);
        }
      });
      
      // Sort lines by Y position (descending - top to bottom)
      const sortedY = Object.keys(lines).map(Number).sort((a, b) => b - a);
      
      // Build page text from lines
      const pageLines = sortedY.map((y) => lines[y].join(' ').trim()).filter(Boolean);
      const pageText = pageLines.join('\n');
      
      // Add page content with appropriate direction
      if (pageText.trim()) {
        const paragraphs = pageText.split('\n').filter(p => p.trim());
        paragraphs.forEach((para) => {
          const paraRTL = detectRTL(para);
          const dirAttr = paraRTL ? ' dir="rtl"' : '';
          htmlParts.push(`<p${dirAttr}>${para}</p>`);
        });
        
        // Add page break between pages (except last page)
        if (pageNum < pdf.numPages) {
          htmlParts.push('<div style="page-break-after: always;"></div>');
        }
      }
    }
    
    // Join all HTML parts
    const htmlContent = htmlParts.join('\n');
    
    // Wrap in HTML structure with UTF-8 encoding
    const wrappedContent = wrapHtmlForWordConversion(htmlContent);
    
    // Convert HTML to Word
    const result = await asBlob(wrappedContent);
    let blob: Blob;
    
    if (result instanceof Blob) {
      blob = result;
    } else if (result && typeof result === 'object' && 'buffer' in result) {
      const arrayBuffer = new Uint8Array(result.buffer as ArrayBuffer);
      blob = new Blob([arrayBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
    } else {
      blob = new Blob([result as BlobPart], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
    }
    
    return blob;
  } catch (error) {
    console.error('Error converting PDF to Word:', error);
    throw new Error(`Failed to convert PDF to Word: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Sanitize filename to remove illegal characters and limit length
 */
export function sanitizeFilename(title: string, index: number, extension: string): string {
  // Remove illegal characters: < > : " / \ | ? *
  let sanitized = title
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '_')
    .trim();
  
  // Limit length to 100 characters
  if (sanitized.length > 100) {
    sanitized = sanitized.substring(0, 100);
  }
  
  // Add index prefix for ordering
  return `${index}_${sanitized}.${extension}`;
}

/**
 * Fetch and convert a single document
 */
async function fetchAndConvertDocument(
  documentId: string,
  format: 'pdf' | 'word',
  proxyDocumentUrl: any
): Promise<{ blob: Blob; title: string }> {
  // Fetch document content via proxy
  const proxyResponse = await proxyDocumentUrl({ documentId }).unwrap();
  const result = proxyResponse?.result;
  
  if (!result || !result.success || !result.content) {
    throw new Error(result?.error || 'Failed to fetch document content');
  }

  // Decode base64 content if encoded (for UTF-8 preservation)
  let content = result.content;
  if (result.encoding === 'base64') {
    try {
      // Decode base64 to get proper UTF-8 string
      const binaryString = atob(content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const decoder = new TextDecoder('utf-8');
      content = decoder.decode(bytes);
    } catch (error) {
      console.error('Failed to decode base64 content:', error);
      // If decoding fails, use content as-is
    }
  }

  const contentType = result.contentType || 'text/html';
  const isSourcePDF = contentType.includes('application/pdf');
  
  // Extract title from content or use documentId
  const title = documentId; // We'll need to pass title from the calling component

  // Convert based on format and source type
  if (isSourcePDF) {
    if (format === 'pdf') {
      // Direct download of PDF (base64 decode - use original content, not decoded)
      const byteCharacters = atob(result.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      return { blob, title };
    } else {
      // Convert PDF to Word by extracting text
      const blob = await convertPdfToWord(result.content, title);
      return { blob, title };
    }
  } else {
    // Source is HTML/text
    if (format === 'pdf') {
      // Strip images and simplify HTML for better PDF rendering
      let cleanedContent = stripImagesFromHtml(content);
      cleanedContent = simplifyHtmlForPdf(cleanedContent);
      
      // Convert HTML to PDF
      const opt = {
        margin: 0.5,
        filename: 'document.pdf',
        image: { type: 'jpeg' as const, quality: 0.95 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          imageTimeout: 0,
          removeContainer: true,
          letterRendering: true
        },
        jsPDF: { 
          unit: 'in', 
          format: 'letter', 
          orientation: 'portrait' as const,
          compress: true
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      
      const pdfBlob = await html2pdf().set(opt).from(cleanedContent).outputPdf('blob');
      return { blob: pdfBlob, title };
    } else {
      // Convert HTML to Word with proper UTF-8 encoding
      const wrappedContent = wrapHtmlForWordConversion(content);
      const result = await asBlob(wrappedContent);
      let blob: Blob;
      
      if (result instanceof Blob) {
        blob = result;
      } else if (result && typeof result === 'object' && 'buffer' in result) {
        const arrayBuffer = new Uint8Array(result.buffer as ArrayBuffer);
        blob = new Blob([arrayBuffer], { 
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
        });
      } else {
        blob = new Blob([result as BlobPart], { 
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
        });
      }
      
      return { blob, title };
    }
  }
}

/**
 * Download multiple documents as a ZIP archive with concurrency control
 */
export async function downloadDocumentsAsZip(
  options: BulkDownloadOptions,
  proxyDocumentUrl: any,
  documentTitles: Map<string, string>
): Promise<{ successCount: number; failedCount: number; errors: Array<{ documentId: string; error: string }> }> {
  const { documentIds, format, zipFilename, onProgress, onError } = options;
  const CONCURRENCY_LIMIT = 5;
  const extension = format === 'pdf' ? 'pdf' : 'docx';
  
  const zip = new JSZip();
  const results: DocumentDownloadResult[] = [];
  const errors: Array<{ documentId: string; error: string }> = [];
  
  // Process documents in batches to limit concurrency
  for (let i = 0; i < documentIds.length; i += CONCURRENCY_LIMIT) {
    const batch = documentIds.slice(i, i + CONCURRENCY_LIMIT);
    
    const batchPromises = batch.map(async (documentId, batchIndex) => {
      const globalIndex = i + batchIndex;
      
      try {
        const { blob, title } = await fetchAndConvertDocument(
          documentId,
          format,
          proxyDocumentUrl
        );
        
        // Get the actual title from the map or use documentId
        const documentTitle = documentTitles.get(documentId) || title || `Document_${globalIndex + 1}`;
        const filename = sanitizeFilename(documentTitle, globalIndex + 1, extension);
        
        // Add to ZIP
        zip.file(filename, blob);
        
        results.push({
          documentId,
          success: true,
          blob,
          filename,
          title: documentTitle
        });
        
        // Report progress
        if (onProgress) {
          onProgress(results.length, documentIds.length);
        }
      } catch (error: any) {
        const errorMsg = error.message || 'Unknown error';
        errors.push({ documentId, error: errorMsg });
        
        results.push({
          documentId,
          success: false,
          error: errorMsg
        });
        
        // Report error
        if (onError) {
          onError(documentId, errorMsg);
        }
        
        // Still report progress
        if (onProgress) {
          onProgress(results.length, documentIds.length);
        }
      }
    });
    
    // Wait for batch to complete
    await Promise.allSettled(batchPromises);
  }
  
  // Check if at least one document was successful
  const successCount = results.filter(r => r.success).length;
  if (successCount === 0) {
    throw new Error('Failed to download any documents');
  }
  
  // Create README.txt with document list
  const readmeContent = generateReadmeContent(results, format);
  zip.file('README.txt', readmeContent);
  
  // Generate ZIP and trigger download
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  
  // Trigger browser download
  const url = window.URL.createObjectURL(zipBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = zipFilename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
  
  return {
    successCount,
    failedCount: errors.length,
    errors
  };
}

/**
 * Generate README.txt content for the ZIP archive
 */
function generateReadmeContent(
  results: DocumentDownloadResult[],
  format: 'pdf' | 'word'
): string {
  const timestamp = new Date().toISOString();
  const successful = results.filter(r => r.success);
  const failed = results.filter(r => !r.success);
  
  let content = `Legal Source Documents Archive\n`;
  content += `Generated: ${timestamp}\n`;
  content += `Format: ${format.toUpperCase()}\n`;
  content += `Total Documents: ${results.length}\n`;
  content += `Successfully Downloaded: ${successful.length}\n`;
  content += `Failed: ${failed.length}\n`;
  content += `\n${'='.repeat(60)}\n\n`;
  
  if (successful.length > 0) {
    content += `SUCCESSFULLY DOWNLOADED DOCUMENTS:\n\n`;
    successful.forEach((result, index) => {
      content += `${index + 1}. ${result.filename}\n`;
      if (result.title) {
        content += `   Title: ${result.title}\n`;
      }
      content += `   Document ID: ${result.documentId}\n\n`;
    });
  }
  
  if (failed.length > 0) {
    content += `\n${'='.repeat(60)}\n\n`;
    content += `FAILED DOWNLOADS:\n\n`;
    failed.forEach((result, index) => {
      content += `${index + 1}. Document ID: ${result.documentId}\n`;
      content += `   Error: ${result.error}\n\n`;
    });
  }
  
  content += `\n${'='.repeat(60)}\n\n`;
  content += `USAGE NOTES:\n`;
  content += `- These documents are legal source materials\n`;
  content += `- Always verify citations with original sources\n`;
  content += `- Documents are numbered for easy reference\n`;
  
  return content;
}

